// 거래명세서 사본 1매. 데이터를 레이아웃으로 옮기기만 하는 순수 컴포넌트다.
// (@/lib · @/stores · @/contexts를 import하지 않는다 — 서버 PDF로 전환해도 재사용 가능해야 한다)

import type {
  InvoiceDocumentDto,
  InvoiceDocumentRow,
  InvoiceParty,
  InvoiceRowType,
} from './invoice-document-types';

export const SUBTOTAL_CAPTION: Record<Exclude<InvoiceRowType, 'detail'>, string> = {
  carSubtotal: '차 량 계',
  oilSubtotal: '유 종 계',
  grandTotal: '총  계',
};

function fmtAmount(value: number) {
  return value.toLocaleString('ko-KR');
}

// 정본과 동일하게 소수 최대 2자리로 표기한다. 유형 6의 수량 정수화는
// 빌더가 라인 단위로 이미 반올림하므로(소계행은 소수를 유지한다) 여기서 자릿수를 나누지 않는다.
function fmtQty(value: number) {
  return value.toLocaleString('ko-KR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function PartyBlock({ label, party }: { label: string; party: InvoiceParty }) {
  return (
    <table className='inv-party'>
      <tbody>
        <tr>
          <td className='inv-party-label' rowSpan={3}>
            {label}
          </td>
          <th>등록NO</th>
          <td className='inv-party-value'>{party.bizNo}</td>
          <th>대표</th>
          <td className='inv-party-value'>{party.ceo}</td>
        </tr>
        <tr>
          <th>상 호</th>
          <td className='inv-party-value' colSpan={3}>
            {party.code ? `${party.code} ${party.name}` : party.name}
          </td>
        </tr>
        <tr>
          <th>소재지</th>
          <td className='inv-party-value' colSpan={3}>
            {party.address}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/** 소계행은 품목명 칸에 캡션을 넣고 차량NO·단가 칸을 비운다. 합계금액은 전 행이 금액 칸이다 */
function rowCells(row: InvoiceDocumentRow) {
  if (row.rowType === 'detail') {
    return {
      dateText: row.dateText,
      itemName: row.itemName,
      unit: row.unit,
      carNo: row.carNo,
      qty: fmtQty(row.qty),
      unitPrice: row.unitPrice === null ? '' : fmtAmount(row.unitPrice),
    };
  }
  return {
    dateText: '',
    itemName: SUBTOTAL_CAPTION[row.rowType],
    unit: '',
    carNo: '',
    qty: fmtQty(row.qty),
    unitPrice: '',
  };
}

interface InvoiceCopyProps {
  header: InvoiceDocumentDto['header'];
  tableTitle: string;
  rows: InvoiceDocumentRow[];
  copyLabel: string;
}

export function InvoiceCopy({
  header,
  tableTitle,
  rows,
  copyLabel,
}: InvoiceCopyProps) {
  const { supplier, customer } = header;

  return (
    <div className='inv-copy'>
      <div className='inv-copy-label'>({copyLabel})</div>
      <div className='inv-title'>{header.titleText}</div>

      <div className='inv-parties'>
        <PartyBlock label='공급자' party={supplier} />
        <PartyBlock label='공급받는자' party={customer} />
        {supplier.sealImageUrl && (
          <img className='inv-seal' src={supplier.sealImageUrl} alt='' />
        )}
      </div>

      <div className='inv-table-title'>{tableTitle}</div>

      <table className='inv-table'>
        <colgroup>
          <col className='inv-col-date' />
          <col className='inv-col-item' />
          <col className='inv-col-unit' />
          <col className='inv-col-car' />
          <col className='inv-col-qty' />
          <col className='inv-col-price' />
          <col className='inv-col-amount' />
          <col className='inv-col-supply' />
          <col className='inv-col-vat' />
        </colgroup>
        <thead>
          <tr>
            <th>월일</th>
            <th>품목명</th>
            <th>단위</th>
            <th>차량NO</th>
            <th>수량</th>
            <th>단가</th>
            <th>금액</th>
            <th>공급가액</th>
            <th>세액</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const cells = rowCells(row);
            return (
              <tr
                key={index}
                className={row.rowType === 'detail' ? undefined : 'inv-subtotal'}
              >
                <td>{cells.dateText}</td>
                <td>{cells.itemName}</td>
                <td>{cells.unit}</td>
                <td>{cells.carNo}</td>
                <td className='inv-num'>{cells.qty}</td>
                <td className='inv-num'>{cells.unitPrice}</td>
                <td className='inv-num'>{fmtAmount(row.totalAmt)}</td>
                <td className='inv-num'>{fmtAmount(row.supplyAmt)}</td>
                <td className='inv-num'>{fmtAmount(row.vatAmt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {supplier.logoImageUrl && (
        <div className='inv-footer'>
          <img src={supplier.logoImageUrl} alt='' />
        </div>
      )}
    </div>
  );
}
